import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import {
  Car,
  Bike,
  Ship,
  Caravan,
  Truck,
  Building2,
  Flag,
  Wrench,
  ArrowRight,
  Plus,
  Shield,
  FileCheck,
  AlertCircle,
} from 'lucide-react';

const CATEGORIES = [
  { slug: 'cars', label: 'Cars', icon: Car, active: true },
  { slug: 'motorbikes', label: 'Motorbikes', icon: Bike, active: false },
  { slug: 'boats', label: 'Boats', icon: Ship, active: false },
  { slug: 'caravans', label: 'Caravans', icon: Caravan, active: false },
  { slug: 'trucks', label: 'Trucks', icon: Truck, active: false },
  { slug: 'commercial', label: 'Commercial', icon: Building2, active: false },
  { slug: 'motorsport', label: 'Motorsport', icon: Flag, active: false },
  {
    slug: 'parts',
    label: 'Parts & Accessories',
    icon: Wrench,
    active: false,
    listLabel: 'List Parts & Accessories',  // default pattern mangles labels ending in "s"
  },
];

export default function AutoHubPage() {
  const { user } = useAuth();

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
        {/* Hero */}
        <div
          className="rounded-2xl p-6 md:p-8 mb-8"
          style={{ background: 'linear-gradient(135deg, #1E3A5F 0%, #2D4A6F 100%)' }}
        >
          <div className="flex items-center gap-3 mb-3">
            <Car className="w-7 h-7 text-[#F8F5F0]" />
            <h1 className="text-2xl md:text-3xl font-bold text-[#F8F5F0]">WA&#1048;TD Auto</h1>
          </div>
          <p className="text-sm md:text-base max-w-lg" style={{ color: 'rgba(248,245,240,0.8)' }}>
            Australia's private vehicle classifieds. Quality-scored listings, PPSR checks bundled on Plus listings.
          </p>
          <div className="flex flex-wrap gap-4 mt-4 text-xs" style={{ color: 'rgba(248,245,240,0.6)' }}>
            <span className="flex items-center gap-1.5"><Shield className="w-3.5 h-3.5" /> Quality-scored</span>
            <span className="flex items-center gap-1.5"><FileCheck className="w-3.5 h-3.5" /> PPSR bundled (Plus)</span>
            <span className="flex items-center gap-1.5"><AlertCircle className="w-3.5 h-3.5" /> Classifieds only</span>
          </div>
        </div>

        {/* Category Grid */}
        <div className="space-y-3">
          {CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            return (
              <div
                key={cat.slug}
                className="grid grid-cols-2 gap-3"
              >
                {/* Browse */}
                {cat.active ? (
                  <Link
                    to={`/auto/${cat.slug}`}
                    className="group flex items-center gap-3 p-4 rounded-xl bg-[var(--theme-card)] border border-[var(--theme-border)] hover:border-[#1E3A5F] transition-all hover:shadow-sm"
                  >
                    <div className="w-10 h-10 rounded-lg bg-[#1E3A5F]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#1E3A5F] transition-colors">
                      <Icon className="w-5 h-5 text-[#1E3A5F] group-hover:text-white transition-colors" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[var(--theme-text-primary)]">Browse {cat.label}</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-[var(--theme-text-muted)] group-hover:text-[#1E3A5F] group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                  </Link>
                ) : (
                  <div
                    className="flex items-center gap-3 p-4 rounded-xl bg-[var(--theme-card)] border border-[var(--theme-border)] opacity-60 cursor-not-allowed"
                    aria-disabled="true"
                  >
                    <div className="w-10 h-10 rounded-lg bg-[var(--theme-hover)] flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-[var(--theme-text-muted)]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[var(--theme-text-secondary)]">Browse {cat.label}</p>
                      <p className="text-xs text-[var(--theme-text-muted)]">Coming Soon</p>
                    </div>
                  </div>
                )}

                {/* List */}
                {cat.active ? (
                  user ? (
                    <Link
                      to={`/auto/${cat.slug}/list`}
                      className="group flex items-center gap-3 p-4 rounded-xl bg-[var(--theme-card)] border border-[var(--theme-border)] hover:border-[#C9956C] transition-all hover:shadow-sm"
                    >
                      <div className="w-10 h-10 rounded-lg bg-[#C9956C]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#C9956C] transition-colors">
                        <Plus className="w-5 h-5 text-[#C9956C] group-hover:text-white transition-colors" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-[var(--theme-text-primary)]">{cat.listLabel || `List a ${cat.label.replace(/s$/, '')}`}</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-[var(--theme-text-muted)] group-hover:text-[#C9956C] group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                    </Link>
                  ) : (
                    <Link
                      to="/login"
                      className="group flex items-center gap-3 p-4 rounded-xl bg-[var(--theme-card)] border border-[var(--theme-border)] hover:border-[#C9956C] transition-all hover:shadow-sm"
                    >
                      <div className="w-10 h-10 rounded-lg bg-[#C9956C]/10 flex items-center justify-center flex-shrink-0">
                        <Plus className="w-5 h-5 text-[#C9956C]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-[var(--theme-text-primary)]">{cat.listLabel || `List a ${cat.label.replace(/s$/, '')}`}</p>
                        <p className="text-xs text-[var(--theme-text-muted)]">Sign in to list</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-[var(--theme-text-muted)] flex-shrink-0" />
                    </Link>
                  )
                ) : (
                  <div className="flex items-center gap-3 p-4 rounded-xl bg-[var(--theme-card)] border border-[var(--theme-border)] opacity-60">
                    <div className="w-10 h-10 rounded-lg bg-[var(--theme-hover)] flex items-center justify-center flex-shrink-0">
                      <Plus className="w-5 h-5 text-[var(--theme-text-muted)]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[var(--theme-text-secondary)]">{cat.listLabel || `List a ${cat.label.replace(/s$/, '')}`}</p>
                      <p className="text-xs text-[var(--theme-text-muted)]">Coming Soon</p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer — Terms & Legal */}
        <div className="mt-10 pt-6 border-t border-[var(--theme-border)]">
          <p className="text-xs text-[var(--theme-text-muted)] mb-3">
            WA&#1048;TD Auto is a classifieds service. WA&#1048;TD is not a party to any vehicle transaction.
            PPSR checks are informational only and do not constitute a guarantee.
          </p>
          <div className="flex flex-wrap gap-4 text-xs">
            <Link to="/auto/terms" className="text-[#1E3A5F] hover:underline font-medium">
              Auto Terms & Conditions
            </Link>
            <Link to="/privacy" className="text-[#1E3A5F] hover:underline font-medium">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-[#1E3A5F] hover:underline font-medium">
              General Terms
            </Link>
          </div>
        </div>
      </div>
    </Layout>
  );
}
